import { useState, useEffect } from "react";
import { useInView } from "react-intersection-observer";
import { ChatWidget } from "@/components/landing2/chat/ChatWidget";

type Punchline = {
  pre: string;
  post: string;
  sub: string;
};

const VIBE_APPS = [
  "a habit tracker for your situationship",
  "a dating app for cats",
  "an AI that rates your fridge",
  "a Notion alternative for therapists",
  "a recipe app that yells at you",
  "a Twitter clone but for plants",
  "a ChatGPT wrapper your mom will love",
  "yet another startup idea generator"
];

const LANDING_CTA_ACTIONS = [
  { label: "Create account & deploy →", href: "/dashboard", variant: "primary" as const },
  { label: "See the plan first", href: "/pricing", variant: "secondary" as const }
];

const useStableTypewriter = (words: string[], active: boolean) => {
  const [text, setText] = useState(words[0] ?? "");
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState<"typing" | "hold" | "erasing">("hold");

  useEffect(() => {
    if (!words.length || !active) {
      return;
    }
    const word = words[idx % words.length];
    let t: ReturnType<typeof setTimeout>;
    if (phase === "typing") {
      if (text.length < word.length) {
        t = setTimeout(() => setText(word.slice(0, text.length + 1)), 60);
      } else {
        t = setTimeout(() => setPhase("hold"), 1600);
      }
    } else if (phase === "hold") {
      t = setTimeout(() => setPhase("erasing"), 1600);
    } else {
      if (text.length > 0) {
        t = setTimeout(() => setText(text.slice(0, -1)), 28);
      } else {
        setIdx(x => x + 1);
        setPhase("typing");
      }
    }
    return () => clearTimeout(t);
  }, [text, phase, idx, words, active]);

  return text;
};

export const Hero = ({ punchline }: { punchline: Punchline }) => {
  const { ref, inView } = useInView();
  const typed = useStableTypewriter(VIBE_APPS, inView);

  return (
    <section className="relative z-[1] px-6 pt-16 pb-22">
      <div className="max-w-[920px] mx-auto text-center">
        <div
          className="inline-flex items-center gap-2 text-xs text-muted-foreground bg-foreground/[0.04] border rounded-full px-3.5 py-1.5 mb-8 font-medium"
          aria-label="For vibe coders"
        >
          <span
            className="w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_0_3px_oklch(0.569_0.195_269_/_0.18)]"
            aria-hidden="true"
          />
          for vibe coders, cursor kids &amp; &ldquo;claude wrote it&rdquo; enjoyers
        </div>

        <h1 className="font-serif font-normal text-[clamp(44px,7vw,76px)] leading-[1.04] tracking-tight mb-5 text-foreground text-balance">
          <div>{punchline.pre}</div>{" "}
          <span
            className="inline align-baseline font-serif italic text-primary"
            aria-live="polite"
            aria-label={`"${typed}"`}
          >
            <span className="text-muted-foreground/60 not-italic" aria-hidden="true">
              &ldquo;
            </span>
            <span className="italic" ref={ref}>
              {typed}
            </span>
            <span
              className="inline-block ml-0.5 text-primary not-italic font-light animate-caret-blink"
              aria-hidden="true"
            >
              |
            </span>
            <span className="text-muted-foreground/60 not-italic" aria-hidden="true">
              &rdquo;
            </span>
          </span>
          <br />
          <span>{punchline.post}</span>
        </h1>

        <p className="text-lg leading-relaxed text-muted-foreground max-w-[560px] mx-auto mb-10 text-pretty">
          {punchline.sub}
        </p>

        <ChatWidget ctaActions={LANDING_CTA_ACTIONS} trackPrefix="L2" />

        <div
          className="flex justify-center items-center gap-3 mt-9 flex-wrap"
          aria-label="Trust indicators"
        >
          <span className="text-xs text-muted-foreground/60">start for free</span>
          <span className="w-1.5 h-1.5 rounded-full bg-primary" aria-hidden="true" />
          <span className="text-xs text-muted-foreground/60">14-day trial plan</span>
          <span className="w-1.5 h-1.5 rounded-full bg-primary" aria-hidden="true" />
          <span className="text-xs text-muted-foreground/60">servers from $4/mo</span>
          <span className="w-1.5 h-1.5 rounded-full bg-primary" aria-hidden="true" />
          <span className="text-xs text-muted-foreground/60">cancel anytime</span>
        </div>
      </div>
    </section>
  );
};
